import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inrMoney, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { BadgeIndianRupee, Download, Building, Store } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function AdminGSTReport() {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (from > to) { toast.error("From date must be before To date"); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/reports/gst-liability?from_date=${from}&to_date=${to}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const exportCSV = () => {
    if (!data) return;
    const header = ["Counter / Vendor", "Operator", "GSTIN", "Orders", "Taxable amount (₹)", "GST collected (₹)", "Liability"];
    const rows = data.by_vendor.map(r => [
      r.vendor_name,
      r.operator_type === "school" ? "School-operated" : "Vendor-operated",
      r.gstin || "—",
      r.order_count,
      r.taxable_amount.toFixed(2),
      r.gst_collected.toFixed(2),
      r.operator_type === "school" ? "School remits" : "Vendor remits",
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gst-liability_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data?.totals;

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Tax · GST</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">GST liability</h1>
            <div className="text-sm text-ink-600 mt-1 max-w-2xl">
              GST collected in the selected period, split by who has to remit it to the government.
              <b> School-operated counters</b> = school's liability. <b>Vendor-operated counters</b> = vendor's liability.
            </div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-500">From</label>
              <input data-testid="gst-from" type="date" value={from} onChange={e => setFrom(e.target.value)}
                className="mt-1 rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-500">To</label>
              <input data-testid="gst-to" type="date" value={to} onChange={e => setTo(e.target.value)}
                className="mt-1 rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>
            <button data-testid="gst-run" onClick={run} disabled={busy}
              className="px-4 py-2 rounded-full bg-brand text-white text-sm font-bold disabled:opacity-60">
              {busy ? "Running…" : "Run"}
            </button>
            {data && (
              <button data-testid="gst-export" onClick={exportCSV}
                className="px-4 py-2 rounded-full bg-ink text-white text-sm font-bold inline-flex items-center gap-2">
                <Download size={14}/> CSV
              </button>
            )}
          </div>
        </div>

        {totals && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-3xl border border-border p-6" data-testid="gst-total-card">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-500">
                <BadgeIndianRupee size={14}/> Total GST collected
              </div>
              <div className="font-heading text-4xl font-extrabold text-ink mt-2">{inrMoney(totals.total_gst_collected)}</div>
              <div className="text-xs text-ink-500 mt-1">{(totals.school_order_count || 0) + (totals.vendor_order_count || 0)} orders</div>
            </div>
            <div className="bg-gradient-to-br from-brand to-red-700 text-white rounded-3xl p-6" data-testid="gst-school-card">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/80">
                <Building size={14}/> School's liability
              </div>
              <div className="font-heading text-4xl font-extrabold mt-2">{inrMoney(totals.school_liability)}</div>
              <div className="text-xs text-white/80 mt-1">{totals.school_order_count} orders · {data?.school?.gstin || "no GSTIN on file"}</div>
              <div className="text-[11px] text-white/70 mt-2">School-operated counters — remitted by {data?.school?.name || "the school"}.</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-amber-700 text-white rounded-3xl p-6" data-testid="gst-vendor-card">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/80">
                <Store size={14}/> Vendor liability
              </div>
              <div className="font-heading text-4xl font-extrabold mt-2">{inrMoney(totals.vendor_liability)}</div>
              <div className="text-xs text-white/80 mt-1">{totals.vendor_order_count} orders</div>
              <div className="text-[11px] text-white/70 mt-2">Vendor-operated counters — each vendor remits using their own GSTIN.</div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="gst-table">
            <thead className="bg-cream-200 text-ink-700">
              <tr>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Counter / Vendor</th>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Operator</th>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">GSTIN</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">Orders</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">Taxable</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">GST collected</th>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Remits</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_vendor || []).map(r => (
                <tr key={r.vendor_id} className="border-t border-border" data-testid={`gst-row-${r.vendor_id}`}>
                  <td className="p-3 font-semibold text-ink">{r.vendor_name}</td>
                  <td className="p-3">
                    {r.operator_type === "school"
                      ? <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-brand text-white">School</span>
                      : <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">Vendor</span>}
                  </td>
                  <td className="p-3 font-mono text-xs text-ink-600">{r.gstin || "—"}</td>
                  <td className="p-3 text-right">{r.order_count}</td>
                  <td className="p-3 text-right text-ink-600">{inrMoney(r.taxable_amount)}</td>
                  <td className="p-3 text-right font-bold">{inrMoney(r.gst_collected)}</td>
                  <td className="p-3 text-xs text-ink-600">{r.operator_type === "school" ? "School" : "Vendor"}</td>
                </tr>
              ))}
              {(!data || data.by_vendor.length === 0) && (
                <tr><td colSpan={7} className="p-10 text-center text-ink-500">No taxable orders in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-ink-500 mt-3">
          Note: refunded portions of orders are deducted proportionally from GST so the report reflects actual liability.
          Cancelled orders are excluded.
        </div>
      </div>
    </AppShell>
  );
}
